import React, {
  useState,
  useEffect,
  useRef,
  SetStateAction,
  useCallback,
  useMemo,
} from "react";
import styled from "styled-components";
import { produce } from "immer";
import { Tooltip } from "antd";

import { useAntTheme } from "hooks/useAntTheme";
import {
  registerHighlightsRef,
  scrollToHighlight,
  getHandleDirection,
} from "pages/media/components/animation/helpers/helpers/";
import { useCuepointsStore } from "pages/media/components/animator/useCuepointsStore";
import { TextSelectorHighlight } from "pages/media/components/storyboard/editor/types";
import { ScriptIndexAnchor } from "pages/media/components/animation/components/ScriptIndexAnchor/";
import { AiStarIcon } from "components/icons";
import { useHighlightsStore } from "pages/media/components/animator/useHighlightsStore";
import {
  // EyeClosedIcon,
  EyeSlashIcon,
  // WarningIcon,
} from "@phosphor-icons/react";
import { useTranslate } from "#src/hooks";
import { usesWordSegmenter } from "helpers/utils";
import { getWordSpans } from "pages/media/helpers/scriptAnchors";

export type Selection = {
  text: string;
  startIndex: number;
  endIndex: number;
  highlightId: string;
  subHighlightId?: string;
  selectedSourceId: string;
  sectionId: string;
  generated: boolean;
};

const TextSelector = ({
  parsedContent,
  fullText,
  sectionId,
  onSelectionsChange,
  onWordClick,
  isRTL = false,
  language,
}: {
  parsedContent: TextSelectorHighlight[];
  fullText: string;
  sectionId: string;
  onSelectionsChange?: (selections: Selection[]) => void;
  onWordClick: (
    text: string,
    textIndex: number,
    sourceId: string,
    selected: boolean
  ) => void;
  isRTL?: boolean;
  language?: string;
}) => {
  const wordSpans = useMemo(
    () => getWordSpans(fullText, language),
    [fullText, language]
  );
  // Chinese/Cantonese words touch, so no spacing is added around them.
  const spaced = !usesWordSegmenter(language);
  const [selections, setSelections] = useState<Selection[] | null>(null);
  const t = useTranslate();

  const [isDragging, setIsDragging] = useState(false);
  const [activeHandle, setActiveHandle] = useState<"start" | "end" | null>(
    null
  );
  const [activeSelectionIndex, setActiveSelectionIndex] = useState<
    number | null
  >(null);
  const [hoveredSourceId, setHoveredSourceId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keeps a ref to the latest selections so event handlers (e.g. handleMouseUp)
  // always read fresh state.
  const selectionsRef = useRef(selections);
  useEffect(() => {
    selectionsRef.current = selections;
  }, [selections]);

  const { currentHighlight, highlights, setCurrentHighlight } =
    useHighlightsStore();

  const { setCurrentCuepoint } = useCuepointsStore();

  const highlightsRef = useRef<Record<string, HTMLElement>>({});

  useEffect(() => {
    if (currentHighlight) {
      scrollToHighlight(highlightsRef, currentHighlight.id);
    }
  }, [currentHighlight]);

  // Initialize selections from parsedContent
  const initialSetSelections = useCallback(() => {
    if (!parsedContent || !wordSpans.length) return [];

    return parsedContent
      .map((chunk) => {
        const text = chunk.content;

        return {
          text: text,
          startIndex: chunk.wordIndex,
          highlightId: chunk.id,
          selectedSourceId: chunk.selectedSourceId,
          sectionId,
          endIndex: chunk.endWordIndex ?? chunk.wordIndex,
          id: chunk.id,
          generated: chunk.generated,
        };
      })
      .filter(Boolean);
  }, [wordSpans, parsedContent, sectionId]);

  useEffect(() => {
    setSelections(initialSetSelections());
  }, [initialSetSelections]);

  // Generate array of words with their selection state
  const words = wordSpans.map(({ raw: char, end }, index) => {
    const nextStart = wordSpans[index + 1]?.start;
    const currentSelection = selections?.find(
      (s) => index >= s.startIndex && index <= s.endIndex
    );

    return {
      char,
      // Keep a space where the original text has one (e.g. "AI" in Chinese).
      spaceAfter: nextStart !== undefined && (spaced || nextStart > end),
      selectionIndex: currentSelection
        ? selections.findIndex(
            (s) => s.highlightId === currentSelection.highlightId
          )
        : undefined,
      index,
      selected: !!currentSelection,
      highlightId: currentSelection?.highlightId,
      sourceId: currentSelection?.selectedSourceId,
      generated: currentSelection?.generated,
    };
  });

  useEffect(() => {
    const handleMouseUp = () => {
      if (isDragging && activeSelectionIndex !== null) {
        // When finished dragging, update the text content of all affected selections
        const updatedSelections = selectionsRef.current.map((selection) => {
          // Slice the original text: joining words with " " would add spaces
          // between Chinese words.
          const updatedText = fullText.slice(
            wordSpans[selection.startIndex]?.start ?? 0,
            wordSpans[selection.endIndex]?.end ?? fullText.length
          );
          return {
            ...selection,
            text: updatedText,
          };
        });

        setSelections(updatedSelections);
        onSelectionsChange?.(updatedSelections);
      }
      setIsDragging(false);
      setActiveHandle(null);
      setActiveSelectionIndex(null);
    };

    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [
    isDragging,
    wordSpans,
    fullText,
    onSelectionsChange,
    activeSelectionIndex,
  ]);

  const handleMouseOver = (index: number) => {
    if (!isDragging || activeHandle === null || activeSelectionIndex === null)
      return;
    const activeSelection = selections[activeSelectionIndex];

    if (!activeSelection) return;

    const handleDirection = getHandleDirection(isRTL, activeHandle);

    if (handleDirection === "start") {
      const previousSelectionEndIndex =
        activeSelectionIndex === 0
          ? -1
          : selections[activeSelectionIndex - 1].endIndex;

      if (
        index <= activeSelection.endIndex &&
        index > previousSelectionEndIndex
      ) {
        setSelections(
          produce((draft) => {
            draft[activeSelectionIndex].startIndex = index;
          })
        );
      }
    } else if (handleDirection === "end") {
      const nextSelectionStartIndex =
        activeSelectionIndex === selections.length - 1
          ? words.length + 1
          : selections[activeSelectionIndex + 1].startIndex;
      if (
        index >= activeSelection.startIndex &&
        index < nextSelectionStartIndex
      ) {
        setSelections(
          produce((draft) => {
            draft[activeSelectionIndex].endIndex = index;
          })
        );
      }
    }
  };

  const handleChooseHighlight = (sourceId: string) => {
    if (currentHighlight?.id !== sourceId) {
      const highlight = highlights?.find((h) => h.id === sourceId);
      setCurrentHighlight(highlight);
      setCurrentCuepoint(sourceId);
    }
  };

  const { theme } = useAntTheme();

  return (
    <ScriptWrapper ref={containerRef}>
      {words.map((word) => {
        const isSelectionStart = selections?.some(
          (s, index) =>
            s.startIndex === word.index && word.selectionIndex === index
        );
        const isSelectionEnd = selections?.some(
          (s, index) =>
            s.endIndex === word.index && word.selectionIndex === index
        );

        const isActiveHandler =
          (currentHighlight && currentHighlight?.id === word?.sourceId) ||
          // while creating a new HL, but not yet linked to a cuepoint with sourceId
          (!currentHighlight && word.highlightId && !word.sourceId);
        const color = isActiveHandler
          ? theme.colorPrimary
          : theme.colorTextDescription;

        let highlightIndex;
        let isHighlightBroken = false;

        if (isSelectionEnd) {
          highlightIndex = highlights?.findIndex(
            (h) => h?.id === word?.sourceId
          );
          const highlight = highlights?.find((h) => h?.id === word?.sourceId);
          isHighlightBroken = highlight?.broken || false;
        }

        return (
          <React.Fragment key={word.index}>
            <CharacterSpan
              selected={word.selected}
              isActive={isActiveHandler}
              isHover={hoveredSourceId === word.sourceId}
              color={color}
              onMouseOver={() => {
                handleMouseOver(word.index);
                if (word.sourceId) setHoveredSourceId(word.sourceId);
              }}
              onMouseLeave={() => setHoveredSourceId(null)}
            >
              {isSelectionStart && (
                <TextHandler
                  position={isRTL ? "end" : "start"}
                  color={color}
                  setIsDragging={setIsDragging}
                  setActiveHandle={setActiveHandle}
                  setActiveSelectionIndex={setActiveSelectionIndex}
                  selectionIndex={word.selectionIndex}
                  onDragStart={() => handleChooseHighlight(word.sourceId)}
                  isActive={isActiveHandler}
                  spaced={spaced}
                />
              )}
              <span
                ref={(el) =>
                  registerHighlightsRef(highlightsRef, word.sourceId, el)
                }
                onClick={(e) => {
                  e.stopPropagation(); // to not trigger the parent blur event
                  onWordClick(
                    word.char,
                    word.index,
                    word.sourceId,
                    word.selected
                  );
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  onWordClick(
                    word.char,
                    word.index,
                    word.sourceId,
                    word.selected
                  );
                }}
                style={{
                  scrollMarginTop: 50,
                  scrollMarginBottom: 50,
                }}
              >
                {word.char}
                {word.spaceAfter ? "\u00A0" : ""}
              </span>
              {isSelectionEnd && (
                <TextHandler
                  position={isRTL ? "start" : "end"}
                  color={color}
                  setIsDragging={setIsDragging}
                  setActiveHandle={setActiveHandle}
                  setActiveSelectionIndex={setActiveSelectionIndex}
                  selectionIndex={word.selectionIndex}
                  onDragStart={() => handleChooseHighlight(word.sourceId)}
                  isActive={isActiveHandler}
                  spaced={spaced}
                />
              )}
            </CharacterSpan>
            {isSelectionEnd && highlightIndex !== -1 && (
              <ScriptIndexAnchor
                background={
                  isActiveHandler
                    ? `${theme.colorPrimary}`
                    : `${theme.colorBgSpotlight}`
                }
                highlightId={word.sourceId}
                error={isHighlightBroken}
              >
                {highlightIndex + 1}

                {word.generated && (
                  <AiStarIcon
                    width={12}
                    height={"auto"}
                    style={{ marginLeft: 1 }}
                    color={
                      isActiveHandler ? "#fff" : "url(#paint0_linear_282_445)"
                    }
                    defs={
                      isActiveHandler ? (
                        ""
                      ) : (
                        <linearGradient
                          id="paint0_linear_282_445"
                          x1="0%"
                          y1="100%"
                          x2="100%"
                          y2="0%"
                        >
                          <stop offset="0%" stopColor="#EE00C2" />
                          <stop offset="100%" stopColor="#5F00E3" />
                        </linearGradient>
                      )
                    }
                  />
                )}
                {isHighlightBroken && (
                  <Tooltip
                    title={t(
                      "components.binder.BinderAction.highlightInvisible"
                    )}
                    placement="top"
                  >
                    <EyeSlashIcon
                      size={14}
                      style={{ marginLeft: 2, cursor: "help" }}
                      color="#fff"
                      weight="bold"
                    />
                  </Tooltip>
                )}
              </ScriptIndexAnchor>
            )}
          </React.Fragment>
        );
      })}
    </ScriptWrapper>
  );
};

const ScriptWrapper = styled.div`
  display: inline;
  line-height: 1.4;
  user-select: none;
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* IE 10+ and Edge */
`;

const CharacterSpan = styled.span<{
  selected: boolean;
  color: string;
  isActive: boolean;
  isHover: boolean;
}>`
  background-color: ${(props) =>
    props.selected
      ? props.isHover
        ? `${props.color}40`
        : `${props.color}70`
      : "unset"};
  position: relative;
  display: inline-block;
  ${(props) => props.selected && `cursor: pointer;`}
  transition: 0.3s;
`;

const TextHandler = ({
  position,
  color,
  setIsDragging,
  setActiveHandle,
  setActiveSelectionIndex,
  selectionIndex,
  onDragStart,
  isActive,
  spaced,
}: {
  position: "start" | "end";
  color: string;
  setIsDragging: React.Dispatch<SetStateAction<boolean>>;
  setActiveHandle: React.Dispatch<SetStateAction<"start" | "end" | null>>;
  setActiveSelectionIndex: React.Dispatch<SetStateAction<number | null>>;
  selectionIndex: number | undefined;
  onDragStart: () => void;
  isActive: boolean;
  spaced: boolean;
}) => {
  // Handle mouse down on handles
  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectionIndex === undefined) return;

    setIsDragging(true);
    setActiveHandle(position);
    setActiveSelectionIndex(selectionIndex);
    onDragStart?.();
  };

  const positionStyles = {
    start: {
      left: 0,
      borderRadius: isActive ? "3px 0 0 3px" : 0,
    },
    end: {
      right: 0,
      borderRadius: isActive ? "0 3px 3px 0" : 0,
    },
  };

  return (
    <>
      {spaced && position === "start" ? "\u00A0" : null}
      <Handler
        style={{
          backgroundColor: color,
          ...positionStyles[position],
        }}
        isActive={isActive}
        onMouseDown={handleMouseDown}
      />
      {spaced && position === "end" ? "\u00A0" : null}
    </>
  );
};

const Handler = styled.span<{ isActive: boolean }>`
  position: absolute;
  width: ${(props) => (props.isActive ? "4px" : "0px")};
  height: ${(props) => (props.isActive ? "120%" : "100%")};
  cursor: ew-resize;
  top: ${(props) => (props.isActive ? "-10%" : 0)};
  z-index: 10;
  transition: all 0.2s ease-in-out;
`;

export default TextSelector;
